import { motion } from "framer-motion";
import { scoreBand } from "./meta";
import InfoTip from "@/components/InfoTip";

export default function HealthspanRing({ score = 0, biologicalAge, chronoAge, longevity, beginner = false, recoverySmart = false }) {
  const pct = Math.min(1, score / 1000);
  const r = 52;
  const circ = 2 * Math.PI * r;
  const band = scoreBand(score);

  return (
    <div className="clay p-8 h-full flex flex-col" data-testid="healthspan-card">
      <div className="flex items-center justify-between">
        <span className="label-eyebrow flex items-center gap-1.5">
          Healthspan Score <InfoTip termId="healthspan-score" />
        </span>
        <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: `${band.color}1a`, color: band.color }}>
          {band.label}
        </span>
      </div>
      {beginner && (
        <p className="text-xs text-muted2 mt-2 leading-relaxed" data-testid="healthspan-beginner-hint">
          One number for how well you're sleeping, moving, eating and recovering. Higher = more healthy years ahead.
        </p>
      )}

      <div className="flex-1 flex items-center justify-center py-4">
        <div className="relative w-60 h-60">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r={r} fill="none" stroke="var(--surface-2)" strokeWidth="9" />
            <motion.circle
              cx="60" cy="60" r={r} fill="none" stroke="url(#ringGrad)" strokeWidth="9" strokeLinecap="round"
              strokeDasharray={circ}
              initial={{ strokeDashoffset: circ }}
              animate={{ strokeDashoffset: circ * (1 - pct) }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            />
            <defs>
              <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="var(--brand)" />
                <stop offset="100%" stopColor="var(--sage)" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <motion.span
              className="font-display font-light text-7xl tracking-tighter"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              data-testid="healthspan-score-value"
            >
              {score}
            </motion.span>
            <span className="label-eyebrow mt-1 flex items-center gap-1">
              of 1000 <InfoTip termId="score-1000" />
            </span>
            {recoverySmart && (
              <span data-testid="recovery-smart-badge"
                title="On hard days, resting well raises your score — that's the point."
                className="mt-2 text-[11px] font-semibold rounded-full px-3 py-1 bg-sage/12 text-sage">
                Recovery-smart day ✓
              </span>
            )}
          </div>
        </div>
      </div>

      {biologicalAge != null && (
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="rounded-2xl bg-secondary/60 p-4 text-center">
            <div className="font-display text-3xl font-light">{biologicalAge}</div>
            <div className="label-eyebrow mt-1 flex items-center justify-center gap-1">Biological age <InfoTip termId="biological-age" /></div>
          </div>
          <div className="rounded-2xl bg-secondary/60 p-4 text-center">
            <div className="font-display text-3xl font-light">{chronoAge}</div>
            <div className="label-eyebrow mt-1">Actual age</div>
          </div>
        </div>
      )}

      {longevity && (
        <div className="rounded-2xl bg-brand/10 p-4 mt-3" data-testid="longevity-card">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-display text-2xl font-light tracking-tight" data-testid="days-added-value">
                +{longevity.days_added_total} <span className="text-sm font-normal">days</span>
              </div>
              <div className="label-eyebrow mt-0.5 flex items-center gap-1">Added to your healthspan <InfoTip termId="days-added" /></div>
            </div>
            {longevity.age_delta > 0 && (
              <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-surface text-brand shrink-0" data-testid="age-delta-chip">
                {longevity.age_delta} yrs younger
              </span>
            )}
          </div>
          {longevity.days_added_today > 0 && (
            <div className="text-xs text-muted2 mt-2" data-testid="days-added-today">
              +{longevity.days_added_today} from today's habits
            </div>
          )}
        </div>
      )}
    </div>
  );
}
